import { Base } from '@studiometa/js-toolkit';
import Card from './Card.js';
import TeamScrollParent from './TeamCardAnimation.js';

/**
 * @typedef {{
 *   $refs: {
 *     name: HTMLElement
 *     description: HTMLElement
 *     quickResume: HTMLElement
 *     fact: HTMLElement[]
 *     cardTitle: HTMLElement[]
 *   },
 *   $children: {
 *     Card: Card[]
 *     TeamScrollParent: TeamScrollParent[]
 *   }
 * }} WishlistPageRefs
 */

/**
 * WishlistPage component - displays year resume data from URL.
 * @augments {Base<WishlistPageRefs>}
 */
export default class WishlistPage extends Base {
  /**
   * Config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'WishlistPage',
    refs: ['name', 'description', 'quickResume', 'fact[]', 'cardTitle[]'],
    components: {
      Card,
      TeamScrollParent,
    },
  };

  /**
   * Mounted hook - populate page with year resume data from localStorage.
   */
  mounted() {
    const storedData = localStorage.getItem('signData');

    if (!storedData) {
      window.location.href = '/';
      return;
    }

    const signData = JSON.parse(storedData);

    this.populateBasicInfo(signData);
    this.populateFacts(signData.year_resume?.facts);
    this.populateTeam(signData.team);
    this.populateCards(signData);
  }

  /**
   * Populate basic info (name, description, quickResume).
   * @param {object} signData Sign data from JSON.
   */
  populateBasicInfo(signData) {
    if (this.$refs.name && signData.name) {
      this.$refs.name.textContent = signData.name;
    }
    if (this.$refs.description && signData.description) {
      this.$refs.description.textContent = signData.description;
    }
    if (this.$refs.quickResume && signData.year_resume?.quick_resume) {
      this.$refs.quickResume.textContent = signData.year_resume.quick_resume;
    }
  }

  /**
   * Populate facts from year_resume.
   * @param {object} facts Facts data from JSON.
   */
  populateFacts(facts) {
    if (!facts || !this.$refs.fact) return;

    const factsArray = [facts.facts_1, facts.facts_2, facts.facts_3];
    const factElements = /** @type {HTMLElement[]} */ (this.$refs.fact);

    factElements.forEach((el, index) => {
      if (factsArray[index]) {
        el.textContent = factsArray[index];
      }
    });
  }

  /**
   * Populate team cards with data from JSON.
   * @param {object} team Team data from JSON.
   */
  populateTeam(team) {
    if (!team) return;

    const cards = this.$el.querySelectorAll('.card--team');
    const persons = [team.person_1, team.person_2, team.person_3];

    cards.forEach((card, index) => {
      const person = persons[index];
      if (!person) return;

      const image = card.querySelector('[data-team="image"]');
      const name = card.querySelector('[data-team="name"]');
      const role = card.querySelector('[data-team="role"]');
      const cap = card.querySelector('[data-team="cap"]');
      const boost = card.querySelector('[data-team="boost"]');

      if (image instanceof HTMLImageElement) {
        image.src = person.image;
        image.alt = person.name;
      }
      if (name) name.textContent = person.name;
      if (role) role.textContent = person.role;
      if (cap) cap.textContent = person.cap;
      if (boost) boost.textContent = person.boost;
    });
  }

  /**
   * Populate cards with data from JSON.
   * @param {object} signData Sign data from JSON.
   */
  populateCards(signData) {
    if (!this.$children.Card) return;

    const cardTitles = /** @type {HTMLElement[]} */ (this.$refs.cardTitle);

    this.$children.Card.forEach((cardComponent, index) => {
      const cardIndex = index + 1;
      const cardData = signData[`card_${cardIndex}`];
      if (!cardData) return;

      const cardEl = cardComponent.$el;

      const image = cardEl.querySelector('.card-image');
      if (image instanceof HTMLImageElement) {
        image.src = cardData.image;
        image.alt = cardData.name;
      }

      const nameEl = cardEl.querySelector('.card-name');
      if (nameEl) {
        nameEl.textContent = cardData.name;
      }

      if (cardTitles && cardTitles[index]) {
        cardTitles[index].textContent = cardData.name;
      }
    });
  }
}
